import 'styled-components/macro';
import React, { FC, useState, useEffect, FormEvent } from 'react';
import { ErrorState, LoadingState, Typography, Button, useTheme, TextField } from '@harver/shared-ui';
import { useLocation } from 'react-router-dom';

import { useContent } from '../../branding/content.context';
import { cancelInterview } from './service';
import { getInterviewDetails } from '../shared/services';
import { INTERVIEW_STATUS } from '../shared/domains';
import { CancelledInterview } from '../shared/views/CancelledInterview';
import { AlreadyUnavailableInterview } from '../shared/views/AlreadyUnavailableInterview';
import { Expired } from '../shared/views/Expired';
import { CompletedInterview } from '../shared/views/CompletedInterview';
import { ContentCard } from '../../components/ContentCard';
import { AutocompleteField } from '../../components/SharedStyledElements';
import CookieBanner from '../../components/CookieBanner';

export const Cancellation: FC = () => {
    const theme = useTheme();
    const { interviewCancellation: LANG } = useContent();

    const params = new URLSearchParams(useLocation().search);
    const calendarEventId = params.get('ref') as string;

    const recruiterCancellationToken = params.get('rct');
    const isRecruiter = Boolean(recruiterCancellationToken);
    const reasons = isRecruiter ? LANG.recruiterReasons : LANG.candidateReasons;
    const defaultReason = reasons[0];

    const [error, setError] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setLoading] = useState(true);
    const [organizationName, setOrganizationName] = useState('');
    const [interviewStatus, setInterviewStatus] = useState<INTERVIEW_STATUS | ''>('');
    const [cancellationInputs, setCancellationInputs] = useState({
        selectedReason: defaultReason,
        message: '',
    });

    const isSubmitDisabled = isSubmitting || !cancellationInputs.selectedReason;

    useEffect(() => {
        getInterviewDetails(calendarEventId)
            .then((resp) => {
                setInterviewStatus(resp.status);

                setOrganizationName(resp.organizationName);
            })
            .catch(() => {
                setError(true);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [calendarEventId]);

    const handleOnReasonSelect = (_, selectedOption: string): void => {
        setCancellationInputs({ ...cancellationInputs, selectedReason: selectedOption });
    };

    const onCancellationMessageInput = (event): void => {
        setCancellationInputs({ ...cancellationInputs, message: event.target.value });
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void | boolean> => {
        e.preventDefault();

        if (isSubmitting) return false;

        try {
            setIsSubmitting(true);

            await cancelInterview(
                calendarEventId,
                recruiterCancellationToken,
                cancellationInputs.selectedReason,
                cancellationInputs.message.trim(),
            );

            setInterviewStatus(INTERVIEW_STATUS.cancelled);
        } catch (err) {
            setError(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) return <LoadingState testId="cancellation.loading" />;

    if (interviewStatus === INTERVIEW_STATUS.cancelled) {
        return <CancelledInterview />;
    }

    if (interviewStatus === INTERVIEW_STATUS.unavailable) {
        return <AlreadyUnavailableInterview />;
    }

    if (interviewStatus === INTERVIEW_STATUS.completed) {
        return <CompletedInterview />;
    }

    if (interviewStatus === INTERVIEW_STATUS.expired) {
        return <Expired />;
    }

    if (error) {
        document.title = 'Something went wrong';
        return <ErrorState testId="cancellation.errorScreen" onReloadClick={() => window.location.reload()} />;
    }

    return (
        <ContentCard>
            <form
                onSubmit={handleSubmit}
                name="cancelForm"
                data-testid="cancel-form"
                css={{
                    margin: `${theme.sizes.xxLarge} 0`,
                }}
            >
                <Typography
                    css={{
                        textAlign: 'center',
                        marginBottom: theme.sizes.medium,
                    }}
                    testId="title"
                    variant="h2"
                >
                    {LANG.title}
                </Typography>

                <Typography
                    css={{
                        textAlign: 'center',
                        marginBottom: theme.sizes.large,
                    }}
                    variant="body2"
                >
                    {LANG.heading.replace('{organizationName}', organizationName)}
                </Typography>

                <AutocompleteField
                    css={{ width: '100%', marginBottom: theme.sizes.large }}
                    value={cancellationInputs.selectedReason}
                    options={reasons}
                    getOptionLabel={(option: string) => option}
                    renderOption={(option: string) => (
                        <span data-testid={`cancellation.option.${option}`}>{option}</span>
                    )}
                    onChange={handleOnReasonSelect}
                    renderInput={(params: unknown): JSX.Element => (
                        <TextField
                            {...params}
                            variant="filled"
                            label={LANG.reasonSelectionLabel}
                            testId="select-cancellation-reason"
                        />
                    )}
                />

                <TextField
                    testId="msg-input"
                    variant="filled"
                    css={{ width: '100%', marginBottom: theme.sizes.large }}
                    onChange={onCancellationMessageInput}
                    multiline={true}
                    rows={4}
                    maxLength={250}
                    placeholder={LANG.messageInputLabel}
                    inputProps={{
                        maxLength: 10,
                    }}
                />

                <div
                    css={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <Button
                        type="submit"
                        color="primary"
                        disabled={isSubmitDisabled}
                        testId="cancel-interview-submit-button"
                    >
                        {LANG.cancelButtonLabel}
                    </Button>
                </div>
            </form>
            <CookieBanner />
        </ContentCard>
    );
};
